import React, { useState } from "react";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, UploadCloud, Loader2, FileText } from "lucide-react";
import {
  addDoc,
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { toast } from "react-toastify";
import { auth, db } from "../../firebase";
import { normalizeUpReraComplaint } from "../utils/normalizeRera";
import featureGatingService from "../../services/featureGatingService";
import { statusPillSolid } from "../../styles/statusPill";

// Dedicated detail view for a UP RERA (up-rera.in) Complaint Status result.
// The backend returns a single rich record (not a list), so we render it in
// sectioned panels mirroring the original site's complaint status page.

const val = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const Section = ({ title, children }) => (
  <div className="mb-6 rounded-xl overflow-hidden border border-white/10 bg-white/5">
    <div className="bg-slate-700/40 px-5 py-3 border-b border-white/10">
      <h2 className="text-blue-300 font-semibold text-base">{title}</h2>
    </div>
    <div className="divide-y divide-white/5">{children}</div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-1 md:gap-4 px-5 py-3">
    <div className="text-slate-500 text-sm">{label}</div>
    <div className="text-white text-sm md:col-span-2 break-words whitespace-pre-line">
      {val(value)}
    </div>
  </div>
);

const GridTable = ({ rows }) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return <div className="px-5 py-3 text-slate-500 text-sm">No records</div>;
  }
  const cols = Object.keys(rows[0]);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-white/5">
            {cols.map((c) => (
              <th
                key={c}
                className="text-left text-slate-600 font-medium px-4 py-2 border-b border-white/10 whitespace-nowrap"
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="odd:bg-white/[0.02]">
              {cols.map((c) => (
                <td
                  key={c}
                  className="text-white px-4 py-2 border-b border-white/5 align-top break-words"
                >
                  {val(r[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const statusClasses = (status = "") => `text-white ${statusPillSolid(status)}`;

// Build hearing reminder + "update next hearing" events for an exported case,
// mirroring the RERA list export so notifications behave identically.
const createHearingEvents = async ({
  user,
  caseId,
  caseNo,
  petitioner,
  respondent,
  nextHearingRaw,
  targetCollection,
}) => {
  const parseHearingDate = (str) => {
    if (!str) return null;
    const t = String(str).trim();
    const iso = t.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})/);
    if (iso) return new Date(+iso[1], +iso[2] - 1, +iso[3]);
    const dmy = t.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    if (dmy) return new Date(+dmy[3], +dmy[2] - 1, +dmy[1]);
    return null;
  };
  const formatYMD = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;

  const eventDate = parseHearingDate(nextHearingRaw);
  if (!eventDate || isNaN(eventDate.getTime())) return;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const hearingDay = new Date(eventDate);
  hearingDay.setHours(0, 0, 0, 0);
  if (hearingDay < today) return;

  const reminderDate = new Date(eventDate);
  reminderDate.setHours(8, 0, 0, 0);
  const eventTimeIST = new Date(eventDate);
  eventTimeIST.setHours(18, 0, 0, 0);
  const now = new Date();

  if (reminderDate > now) {
    const reminderEventRef = await addDoc(
      collection(db, "lawyers", user.uid, "events"),
      {
        "Case No": caseNo,
        date: formatYMD(reminderDate),
        id: Math.floor(Math.random() * 90000) + 10000,
        time: "08:00 AM",
        time1: reminderDate.getTime(),
        title: `${petitioner} VS ${respondent}`,
        description: `Hearing Reminder : Case No. - ${caseNo}`,
        isScheduled: true,
        status: targetCollection,
        eventType: "reminder",
      }
    );
    await addDoc(collection(db, "eventReminders"), {
      eventId: reminderEventRef.id,
      reminderTime: reminderDate,
      eventTitle: `Hearing Reminder : Case No. - ${caseNo}`,
      caseNo,
      caseId,
      collection: targetCollection,
      status: "pending",
      createdAt: new Date(),
      scheduledBy: user.uid,
      recipientId: user.uid,
    });
  }

  if (eventTimeIST > now) {
    const mainEventRef = await addDoc(
      collection(db, "lawyers", user.uid, "events"),
      {
        "Case No": caseNo,
        date: formatYMD(eventDate),
        id: Math.floor(Math.random() * 90000) + 10000,
        time: "06:00 PM",
        time1: eventTimeIST.getTime(),
        title: `Update Next Hearing: ${petitioner} VS ${respondent}`,
        description: `Case No. - ${caseNo}`,
        isScheduled: true,
        status: targetCollection,
        eventType: "updateReminder",
        isHidden: true,
      }
    );
    await addDoc(collection(db, "eventReminders"), {
      eventId: mainEventRef.id,
      reminderTime: eventTimeIST,
      eventTitle: `Update Next Hearing : ${caseNo}`,
      caseNo,
      caseId,
      collection: targetCollection,
      status: "pending",
      createdAt: new Date(),
      scheduledBy: user.uid,
      recipientId: user.uid,
    });
  }
};

function UpReraComplaintDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const detail = location.state?.detail;
  const [exporting, setExporting] = useState(false);

  // Export this complaint as a case → firestore (with feature gating +
  // timestamp + hearing events), then open it on the shared case page.
  const exportCase = async () => {
    const user = auth.currentUser;
    if (!user) {
      toast.error("User not authenticated");
      return;
    }
    if (!detail?.complaint_number) {
      toast.error("No complaint to export");
      return;
    }

    const filingNo = detail.complaint_number;

    try {
      setExporting(true);

      // Duplicate check — if already exported, just open it.
      const [pendingSnap, disposedSnap] = await Promise.all([
        getDocs(
          query(
            collection(db, "pending"),
            where("filingNo", "==", filingNo),
            where("owner", "==", user.uid)
          )
        ),
        getDocs(
          query(
            collection(db, "disposed"),
            where("filingNo", "==", filingNo),
            where("owner", "==", user.uid)
          )
        ),
      ]);
      if (!pendingSnap.empty || !disposedSnap.empty) {
        const existing = (!pendingSnap.empty ? pendingSnap : disposedSnap)
          .docs[0];
        toast.info("Case already exported — opening it");
        navigate(`/portal/case/${existing.id}`);
        return;
      }

      const normalized = normalizeUpReraComplaint(detail);
      const c = detail.complainant || {};
      const targetCollection =
        normalized.status === "disposed" ? "disposed" : "pending";

      const caseDataToExport = {
        ...normalized,
        rawReraData: null,
        owner: user.uid,
        team: [user.uid],
        createdAt: new Date(),
        partyContactNumber:
          c.mobile && !/^\*+$/.test(c.mobile) ? c.mobile : "",
      };

      // Subscription metadata (pending cases beyond the always-unlocked tier).
      let subscriptionMetadata = null;
      if (targetCollection === "pending") {
        const pendingAll = await getDocs(
          query(collection(db, "pending"), where("owner", "==", user.uid))
        );
        subscriptionMetadata =
          await featureGatingService.getSubscriptionMetadataForNewCase(
            user.uid,
            pendingAll.size
          );
      }

      caseDataToExport.payload = {
        exportedAt: new Date().toISOString(),
        caseexportedfrom: "UpRera",
        upRera: {
          complaintNumber: detail.complaint_number || "",
          registrationDate: detail.registration_date || "",
          statusText: detail.status || "",
          hearingDetails: detail.hearing_details || "",
          complaintAgainst: detail.complaint_against || "",
          projectDetails: detail.project_details || "",
          competentAuthority: detail.competent_authority || "",
          district: detail.district || "",
          complainantName: c.name || "",
          complainantAddress: c.address || "",
          complainantMobile: c.mobile || "",
          complainantEmail: c.email || "",
          apartmentPlotNo: detail.apartment_plot_no || "",
          totalValue: detail.total_value || "",
          amountPaidTillDate: detail.amount_paid_till_date || "",
          gracePeriod: detail.grace_period || "",
          reliefSoughtType: detail.relief_sought_type || "",
          interimReliefRequest: detail.interim_relief_request || "",
          feePaid: detail.fee_paid || "",
          feePaymentDate: detail.fee_payment_date || "",
          documentaryProofs: Array.isArray(detail.documentary_proofs)
            ? detail.documentary_proofs
            : [],
          groundsForComplaint: Array.isArray(detail.grounds_for_complaint)
            ? detail.grounds_for_complaint
            : [],
          noticeUrl: detail.notice_url || "",
        },
      };
      if (subscriptionMetadata) {
        caseDataToExport.subscriptionMetadata = subscriptionMetadata;
      }
      caseDataToExport.timestamp = serverTimestamp();

      // Case-limit gating.
      const limitResult =
        await featureGatingService.checkCaseLimitBeforeExport(
          user.uid,
          1,
          targetCollection === "disposed" ? "disposed" : "pending"
        );
      if (!limitResult.canProceed) {
        toast.error(
          `Case limit reached! Your ${limitResult.planName} plan allows ${limitResult.maxLimit} ${targetCollection} cases.`
        );
        return;
      }

      const docRef = await addDoc(
        collection(db, targetCollection),
        caseDataToExport
      );
      const caseId = docRef.id;
      await updateDoc(docRef, {
        caseFilesLink: `/portal/case-files/${caseId}?from=rera`,
      });

      try {
        await createHearingEvents({
          user,
          caseId,
          caseNo: filingNo,
          petitioner: normalized.petitionerName,
          respondent: normalized.respondentName,
          nextHearingRaw: normalized.nextHearingDate,
          targetCollection,
        });
      } catch (evtErr) {
        console.error("UP RERA event setup error:", evtErr);
      }

      // Save the complaint notice into the case's "orders" folder.
      try {
        if (detail.notice_url) {
          await addDoc(collection(db, "files"), {
            caseNo: filingNo,
            filingNo,
            fileName: `Notice – ${filingNo}`,
            file_url: detail.notice_url,
            lawyer_uid: user.uid,
            uploadedAt: new Date().toISOString(),
            source: "RERA",
            folder: "orders",
          });
        }
      } catch (fileErr) {
        console.error("UP RERA notice file save error:", fileErr);
      }

      toast.success("Case exported successfully");
      navigate(`/portal/case/${caseId}`);
    } catch (e) {
      console.error("UP RERA export error:", e);
      toast.error("Export failed: " + (e?.message || "Unknown error"));
    } finally {
      setExporting(false);
    }
  };

  if (!detail) {
    return (
      <>
                <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-white text-lg">No complaint data available</p>
            <Link
              to="/portal/fetchfromrera"
              className="text-yellow-500 hover:underline mt-2 inline-block"
            >
              Return to Search
            </Link>
          </div>
        </div>
      </>
    );
  }

  const c = detail.complainant || {};

  return (
    <>
            <div className="relative min-h-screen flex flex-col px-4 md:px-16 py-8">
          <div className="mb-4"><FindCaseStepper current={4} /></div>
        {/* Header */}
        <div className="relative z-10 flex items-center justify-between mb-6">
          <Link to="/portal/fetchfromrera" className="flex items-center">
            <ArrowLeft className="w-6 h-6 text-white cursor-pointer" />
            <h1 className="text-yellow-500 ml-4 font-bold text-2xl">
              Complaint Status
            </h1>
          </Link>
          <div className="flex items-center gap-3">
            {detail.status && (
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${statusClasses(
                  detail.status
                )}`}
              >
                {detail.status}
              </span>
            )}
            {detail.notice_url && (
              <a
                href={detail.notice_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-slate-600 hover:text-white py-2 px-3 font-medium"
              >
                <FileText className="w-4 h-4" />
                View Notice
              </a>
            )}
            <button
              type="button"
              onClick={exportCase}
              disabled={exporting}
              className="flex items-center gap-2 bg-white text-black py-2 px-6 rounded-full font-bold hover:bg-gray-200 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {exporting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Exporting…
                </>
              ) : (
                <>
                  <UploadCloud className="w-4 h-4" />
                  Export to Case
                </>
              )}
            </button>
          </div>
        </div>

        {/* Complaint Current Status */}
        <Section title="Complaint Current Status">
          <Row label="Complaint Number" value={detail.complaint_number} />
          <Row label="Date of Registration" value={detail.registration_date} />
          <Row label="Status" value={detail.status} />
          {detail.hearing_details && (
            <Row label="Hearing Details" value={detail.hearing_details} />
          )}
        </Section>

        {/* Complaint Against Details */}
        <Section title="Complaint Against Details">
          <Row label="Complaint Against" value={detail.complaint_against} />
          <Row label="Project Details" value={detail.project_details} />
          <Row label="Competent Authority" value={detail.competent_authority} />
          <Row label="District" value={detail.district} />
        </Section>

        {/* Complainant Details */}
        <Section title="Complainant Details">
          <Row label="Name" value={c.name} />
          <Row label="Address" value={c.address} />
          <Row label="Mobile" value={c.mobile} />
          <Row label="Email" value={c.email} />
        </Section>

        {/* Complaint / Relief Details */}
        <Section title="Complaint & Relief Details">
          <Row label="Apartment / Plot No" value={detail.apartment_plot_no} />
          <Row label="Total Value" value={detail.total_value} />
          <Row label="Amount Paid Till Date" value={detail.amount_paid_till_date} />
          <Row label="Grace Period" value={detail.grace_period} />
          <Row label="Relief Sought Type" value={detail.relief_sought_type} />
          <Row label="Interim Relief Request" value={detail.interim_relief_request} />
          <Row label="Fee Paid" value={detail.fee_paid} />
          <Row label="Fee Payment Date" value={detail.fee_payment_date} />
        </Section>

        {/* Documentary Proofs */}
        <Section title="Documentary Proofs">
          <GridTable rows={detail.documentary_proofs} />
        </Section>

        {/* Grounds for Complaint */}
        <Section title="Grounds for Complaint">
          <GridTable rows={detail.grounds_for_complaint} />
        </Section>
      </div>
    </>
  );
}

export default UpReraComplaintDetail;
